import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { useCompany } from "../Company/CompanyContext";
import { toast } from "react-toastify";

const TOGGLE_FIELDS = [
  { key: "pdf_show_siret",            tkey: "showSiret" },
  { key: "pdf_show_iban",             tkey: "showIban" },
  { key: "pdf_show_tiers_table",      tkey: "showTiers" },
  { key: "pdf_show_cgv",              tkey: "showCgv" },
  { key: "pdf_show_precious_objects", tkey: "showPrecious" },
];

export default function PdfTab({ raw, onSaved }) {
  const { t } = useTranslation();
  const { reload } = useCompany();
  const [form, setForm] = useState({
    vat_rate:                 raw.vat_rate                  || "0.2",
    quote_validity_days:      raw.quote_validity_days        || "30",
    pdf_accent_color:         raw.pdf_accent_color           || "#33BBFF",
    quote_prefix:             raw.quote_prefix               || "DEV",
    invoice_prefix:           raw.invoice_prefix             || "FAC",
    pdf_payment_terms:        raw.pdf_payment_terms          || "",
    pdf_signature_instruction:raw.pdf_signature_instruction  || "",
    pdf_footer_custom:        raw.pdf_footer_custom          || "",
    cgv_html:                 raw.cgv_html                   || "",
    pdf_show_siret:           raw.pdf_show_siret             !== "false",
    pdf_show_iban:            raw.pdf_show_iban              !== "false",
    pdf_show_tiers_table:     raw.pdf_show_tiers_table       !== "false",
    pdf_show_cgv:             raw.pdf_show_cgv               !== "false",
    pdf_show_precious_objects:raw.pdf_show_precious_objects  !== "false",
  });
  const [saving, setSaving] = useState(false);

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      vat_rate:            String(parseFloat(form.vat_rate) || 0.2),
      quote_validity_days: String(parseInt(form.quote_validity_days) || 30),
      pdf_show_siret:            String(form.pdf_show_siret),
      pdf_show_iban:             String(form.pdf_show_iban),
      pdf_show_tiers_table:      String(form.pdf_show_tiers_table),
      pdf_show_cgv:              String(form.pdf_show_cgv),
      pdf_show_precious_objects: String(form.pdf_show_precious_objects),
    };
    try {
      await api.put("/settings", payload);
      await reload();
      toast.success(t("settings.pdf.saved"));
      onSaved?.();
    } catch {
      toast.error(t("settings.common.saveError"));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      {/* Facturation */}
      <section className="border border-base-300 rounded-xl p-4 space-y-4">
        <h4 className="font-semibold text-sm text-base-content/60 uppercase tracking-wider">{t("settings.pdf.billing")}</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content/70">{t("settings.pdf.vatRate")}</label>
            <input type="number" name="vat_rate" step="0.01" min="0" max="1"
              value={form.vat_rate} onChange={handle}
              className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
            />
            <span className="text-xs text-base-content/40">{t("settings.pdf.vatHint")}</span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content/70">{t("settings.pdf.validity")}</label>
            <input type="number" name="quote_validity_days" min="1" max="365"
              value={form.quote_validity_days} onChange={handle}
              className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content/70">{t("settings.pdf.quotePrefix")}</label>
            <input type="text" name="quote_prefix" maxLength={10}
              value={form.quote_prefix} onChange={handle}
              className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
              placeholder="DEV"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content/70">{t("settings.pdf.invoicePrefix")}</label>
            <input type="text" name="invoice_prefix" maxLength={10}
              value={form.invoice_prefix} onChange={handle}
              className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
              placeholder="FAC"
            />
          </div>
        </div>
        <p className="text-xs text-base-content/40">{t("settings.pdf.numberingHint")}</p>
      </section>

      {/* Couleur & affichage */}
      <section className="border border-base-300 rounded-xl p-4 space-y-4">
        <h4 className="font-semibold text-sm text-base-content/60 uppercase tracking-wider">{t("settings.pdf.appearance")}</h4>
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content/70">{t("settings.pdf.accentColor")}</label>
            <div className="flex items-center gap-2">
              <input type="color" name="pdf_accent_color" value={form.pdf_accent_color} onChange={handle}
                className="w-12 h-10 rounded cursor-pointer border border-base-300"
              />
              <input type="text" name="pdf_accent_color" value={form.pdf_accent_color} onChange={handle}
                className="input input-bordered input-sm bg-base-200 border-base-300 w-32 font-mono"
                placeholder="#33BBFF"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {TOGGLE_FIELDS.map(({ key, tkey }) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" name={key} checked={form[key]} onChange={handle} className="checkbox checkbox-primary checkbox-sm" />
              <span className="text-sm">{t(`settings.pdf.${tkey}`)}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Textes libres */}
      <section className="border border-base-300 rounded-xl p-4 space-y-4">
        <h4 className="font-semibold text-sm text-base-content/60 uppercase tracking-wider">{t("settings.pdf.texts")}</h4>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-base-content/70">{t("settings.pdf.paymentTerms")}</label>
          <textarea name="pdf_payment_terms" rows={3} value={form.pdf_payment_terms} onChange={handle}
            className="textarea textarea-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none resize-y"
            placeholder={t("settings.pdf.paymentPh")}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-base-content/70">{t("settings.pdf.signature")}</label>
          <textarea name="pdf_signature_instruction" rows={2} value={form.pdf_signature_instruction} onChange={handle}
            className="textarea textarea-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none resize-y"
            placeholder={t("settings.pdf.signaturePh")}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-base-content/70">{t("settings.pdf.footer")}</label>
          <textarea name="pdf_footer_custom" rows={2} value={form.pdf_footer_custom} onChange={handle}
            className="textarea textarea-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none resize-y"
            placeholder={t("settings.pdf.footerPh")}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-base-content/70">
            {t("settings.pdf.cgvLabel")}
            <span className="text-xs font-normal text-base-content/40 ml-2">{t("settings.pdf.cgvHint")}</span>
          </label>
          <textarea name="cgv_html" rows={10} value={form.cgv_html} onChange={handle}
            className="textarea textarea-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none font-mono text-xs resize-y"
            placeholder={t("settings.pdf.cgvPh")}
          />
        </div>
      </section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn btn-primary">
          {saving ? t("settings.common.saving") : t("settings.common.save")}
        </button>
      </div>
    </div>
  );
}
